import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Heart, Users, Briefcase, Sparkles, Plus, Search,
  Globe, User, Loader2, Check, Star, ChevronRight, Trash2, Pencil, Volume2
} from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { IdleLoopVideo } from '../components/chat/IdleLoopVideo';
import MilestoneRibbon from '../components/teen/MilestoneRibbon';
import {
  AVA_IMAGES,
  PRESET_AVAS,
  CATEGORIES,
  ALL_PERSONALITY_OPTIONS,
  PERSONALITY_BY_TAB,
  LANGUAGES,
  TEEN_VOICES,
  WEALTH_STATES,
  presetGender,
  presetTeenAge,
  presetDemographicLabel,
} from './avaLibrary/data';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slugify an AvA name for shareable URLs (e.g. "Maya" → "maya").
const avaSlug = (name) => (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Formats a clean AvA display name for library cards.
 * Uses actual image name from DB, cleaned up for display.
 */
function formatAvaDescription(img) {
  // Use the actual name from the database
  if (img.name) {
    // Clean up long descriptive names — take first ~30 chars
    let name = img.name.trim();
    // Remove "Californina" typo prefix if present
    name = name.replace(/^Californina\s+/i, '');
    // Capitalize first letter of each word
    return name.length > 35 ? name.slice(0, 32) + '...' : name;
  }

  // Fallback: category-based label
  if (img.category === 'wealth') {
    const bizTitle = img.gender === 'female' ? 'Businesswoman' : 'Businessman';
    return `Practice ${bizTitle}`;
  }

  return 'Practice AvA';
}

/**
 * Returns the AvA subtitle (category/folder info) for display below the name.
 */
function formatAvaSubtitle(img) {
  const parts = [];
  if (img.category) parts.push(img.category.charAt(0).toUpperCase() + img.category.slice(1));
  if (img.gender) parts.push(img.gender.charAt(0).toUpperCase() + img.gender.slice(1));
  return parts.join(' · ') || 'Practice AvA';
}

/**
 * Returns the default AvA name based on category and page number.
 */
function getDefaultAvaName(category, pageNum) {
  const state = WEALTH_STATES[(pageNum - 1) % WEALTH_STATES.length];
  return `${state} AvA`;
}

// Aug 2026 — Specialist tutor library: canonical order + card subtitles.
const SPECIALIST_META = {
  specialist_leo:    { order: 3, subtitle: 'Relationship Communication' },
  specialist_marcus: { order: 4, subtitle: 'Career & Authority' },
  specialist_chloe:  { order: 5, subtitle: 'Social Dynamics & Conflict' },
  specialist_ethan:  { order: 6, subtitle: 'Public Speaking & Voice' },
  specialist_sarah:  { order: 7, subtitle: 'Organization & Boundaries' },
  specialist_petra:  { order: 8, subtitle: 'Digital Life & Online Safety' },
};

const AvALibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTeen = user?.age_band === 'teen';
  const [activeCategory, setActiveCategory] = useState(isTeen ? 'happiness' : 'all');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userAvas, setUserAvas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // Sprint 207AA — centered red/black modal in place of window.alert().
  const [saveErrorModal, setSaveErrorModal] = useState(null);
  const [customPhotoUrl, setCustomPhotoUrl] = useState(null);
  const [editingAva, setEditingAva] = useState(null); // For edit mode
  const [previewingVoice, setPreviewingVoice] = useState(null); // Voice being previewed
  const [previewAudio, setPreviewAudio] = useState(null); // Audio element ref
  const [matchedTypes, setMatchedTypes] = useState([]); // OCEAN matched AvA types

  // Image Library state
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryFilters, setLibraryFilters] = useState(null);
  const [libCategory, setLibCategory] = useState('');
  const [libGender, setLibGender] = useState('male');
  const [libAgeRange, setLibAgeRange] = useState('');
  const [libRoleType, setLibRoleType] = useState(() => (user?.age_band === 'teen' ? 'friend' : 'tutor'));
  const [libSearch, setLibSearch] = useState('');
  const [libPage, setLibPage] = useState(1);
  const [libTotal, setLibTotal] = useState(0);
  const [libLoading, setLibLoading] = useState(false);
  const [showLibrary] = useState(true);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [previewAvaLibId, setPreviewAvaLibId] = useState(null);
  const [previewAnimating, setPreviewAnimating] = useState(false);
  const [libPageState, setLibPageState] = useState(null);

  // Mobile UX — when teen taps an AvA in the library, scroll the right-hand
  // Customize panel into view so they don't have to thumb past the grid.
  const customizePanelRef = React.useRef(null);
  const scrollToCustomize = React.useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      // small timeout lets the preview image swap before we measure scroll.
      setTimeout(() => {
        customizePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, []);

  const isCorporateUser = !!(user?.org_id || user?.company_id);
  const libFetchSeq = React.useRef(0);

  // Fetch library images. Wrapped in useCallback so the mount+filter
  // useEffect below can list it in deps without triggering an infinite
  // reload — the reference changes ONLY when a filter/page value moves.
  const fetchLibrary = useCallback(async () => {
    setLibLoading(true);
    const seq = ++libFetchSeq.current;
    try {
      const params = new URLSearchParams({ page: libPage, limit: 24 });
      if (libCategory) params.append('category', libCategory);
      if (libGender) params.append('gender', libGender);
      if (libAgeRange) params.append('age_range', libAgeRange);
      if (isTeen && libRoleType) params.append('role_type', libRoleType);
      if (libSearch) params.append('search', libSearch);
      const data = await apiClient.request(`/api/ava-library/browse?${params}`);
      if (seq !== libFetchSeq.current) return; // stale response — a newer filter fetch is in flight
      setLibraryImages(data.images || []);
      setLibTotal(data.total || 0);
      setLibPageState(data.page_state || null);
    } catch (e) {
      console.error('Library fetch failed:', e);
    }
    if (seq === libFetchSeq.current) setLibLoading(false);
  }, [libPage, libCategory, libGender, libAgeRange, libSearch, libRoleType, isTeen]);

  // Wrapped in useCallback([]) — reads no closure state. Locked reference
  // means it's safe to name in the caller effect's deps.
  const fetchLibraryFilters = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/ava-library/filters');
      setLibraryFilters(data);
    } catch (e) { /* filters endpoint missing — component falls back to defaults */ }
  }, []);

  useEffect(() => {
    if (isTeen || showLibrary) {
      fetchLibrary();
      if (!libraryFilters) fetchLibraryFilters();
    }
    // Note: `libCategory`, `libGender`, `libAgeRange`, `libSearch`, `libPage`
    // are already captured inside `fetchLibrary` via its own useCallback deps,
    // so we don't need to re-list them here — passing `fetchLibrary` is enough.
    // `libraryFilters` triggers the one-shot filter fetch (guarded by `!libraryFilters`).
  }, [isTeen, showLibrary, fetchLibrary, fetchLibraryFilters, libraryFilters]);

  // Customization form
  const [customization, setCustomization] = useState({
    userName: user?.name?.split(' ')[0] || '',
    avaName: '',
    personality: 'Encouraging',
    language: 'en',
    voice: 'tf1',
    backstory: '',
    libraryImageId: null
  });

  // Fetch idle loop video for the currently selected preview image
  useEffect(() => {
    const imageUrl = customPhotoUrl || (selectedPreset ? getAvaUrl(selectedPreset) : null);
    if (!imageUrl || imageUrl.startsWith('data:')) {
      setPreviewVideoUrl(null);
      setPreviewAvaLibId(null);
      return;
    }
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const fetchVideo = async () => {
      try {
        const data = await apiClient.request('/api/ava-video/idle-loop-by-image', {
          method: 'POST',
          body: JSON.stringify({ image_url: imageUrl }),
        });
        if (cancelled) return;
        if (data?.idle_loop_url) {
          setPreviewVideoUrl(`${API_URL}/api/files/${data.idle_loop_url}`);
          setPreviewAvaLibId(data.ava_library_id);
          setPreviewAnimating(false);
        } else {
          setPreviewVideoUrl(null);
          setPreviewAvaLibId(data?.ava_library_id || null);
          // Lazy generation (Oct 2026): the backend kicks off idle-loop
          // generation on first pick — poll until the animation is ready.
          if (data?.generating && attempts < 20) {
            attempts += 1;
            setPreviewAnimating(true);
            timer = setTimeout(fetchVideo, 15000);
          } else {
            setPreviewAnimating(false);
          }
        }
      } catch {
        if (!cancelled) {
          setPreviewVideoUrl(null);
          setPreviewAvaLibId(null);
          setPreviewAnimating(false);
        }
      }
    };
    fetchVideo();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [customPhotoUrl, selectedPreset]);

  // Load user's existing AVAs — retries twice on transient route-transition
  // failures (iter28/29: page rendered empty although the API was healthy).
  useEffect(() => {
    let cancelled = false;
    const fetchAvas = async (attempt = 0) => {
      try {
        const avas = await apiClient.getAvas();
        if (cancelled) return;
        setUserAvas(avas);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        if (attempt < 2) {
          setTimeout(() => fetchAvas(attempt + 1), 1000 * (attempt + 1));
        } else {
          console.error('Failed to load AVAs:', error);
          setLoading(false);
        }
      }
    };
    fetchAvas();
    return () => { cancelled = true; };
  }, []);

  // Sprint 207P — fetch idle-loop video URL for each user AvA so the
  // 'My Trainers' tiles animate (not just the chat-screen avatar).
  // Cached per ava_id; misses fall back to the static portrait silently.
  const [trainerIdleLoops, setTrainerIdleLoops] = useState({}); // { ava_id: url }

  // Derive a stable string key from the current userAvas list so the effect
  // below only re-fires when the set of ava_ids actually changes (not on
  // every parent re-render). Extracted with useMemo to satisfy the linter's
  // "complex expression in dependency array" warning cleanly.
  const userAvaIdsKey = React.useMemo(
    () => (userAvas || []).map(a => a.ava_id).sort().join('|'),
    [userAvas]
  );

  useEffect(() => {
    if (!userAvas?.length) return;
    let cancelled = false;
    (async () => {
      try {
        // ONE batch request for every trainer tile. The previous pattern
        // (N parallel /idle-loop-for-ava/{id} calls) tripped the global
        // rate limiter (429) and silently left the tiles static.
        const data = await apiClient.request('/api/ava-video/idle-loops-for-user');
        if (cancelled) return;
        const results = {};
        Object.entries(data?.idle_loops || {}).forEach(([avaId, url]) => {
          results[avaId] = `${API_URL}/api/files/${url}`;
        });
        setTrainerIdleLoops(results);
      } catch { /* fail-soft: tiles keep static images */ }
    })();
    return () => { cancelled = true; };
    // We intentionally key on `userAvaIdsKey` (derived stable string) rather
    // than `userAvas` (array identity flips on every parent re-render).
    // Verified: only re-fires when the actual set of ava_ids changes.
  }, [userAvaIdsKey]);

  // Load OCEAN matched types
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const data = await apiClient.request('/api/ocean/ava-matches');
        if (data.matched_types) setMatchedTypes(data.matched_types);
      } catch { /* silent */ }
    };
    fetchMatches();
  }, []);

  // Get filtered avatars based on category
  const getFilteredAvas = () => {
    let avatars = [];
    const isTeen = user?.age_band === 'teen';

    if (activeCategory === 'all') {
      if (isTeen) {
        // Teens only see happiness category
        avatars = [...PRESET_AVAS.happiness];
      } else {
        avatars = [
          ...PRESET_AVAS.health,
          ...PRESET_AVAS.wealth,
          ...PRESET_AVAS.happiness
        ];
      }
    } else {
      avatars = PRESET_AVAS[activeCategory] || [];
    }

    if (searchQuery) {
      avatars = avatars.filter(a =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return avatars;
  };

  const getAvaUrl = (avatar) => {
    // Use photorealistic image if available, otherwise fall back to DiceBear
    if (avatar.image) {
      return avatar.image;
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${avatar.name}&backgroundColor=b6e3f4,c0aede,ffd5dc`;
  };

  const getAvaRole = (presetId) => {
    if (presetId?.startsWith('health')) return 'health';
    if (presetId?.startsWith('wealth')) return 'wealth';
    if (presetId?.startsWith('happiness')) return 'happiness';
    return 'health';
  };


  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setCustomPhotoUrl(null); // Clear custom photo
    setEditingAva(null); // Clear edit mode
    setCustomization(prev => ({
      ...prev,
      avaName: preset.name,
      personality: preset.personality
    }));
  };

  // Handle editing an existing AvA
  const handleEditAva = (ava, e) => {
    e.stopPropagation();
    setEditingAva(ava);
    setSelectedPreset(null);
    setCustomPhotoUrl(ava.avatar_url || null);
    setCustomization({
      userName: user?.name?.split(' ')[0] || '',
      avaName: ava.name,
      personality: ava.personality_type || 'Encouraging',
      language: ava.avatar_config?.language || 'en',
      voice: ava.avatar_config?.voice || 'tf1',
      backstory: ava.avatar_config?.backstory || ''
    });
    // Scroll to customization panel
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditingAva(null);
    setSelectedPreset(null);
    setCustomPhotoUrl(null);
    setCustomization({
      userName: user?.name?.split(' ')[0] || '',
      avaName: '',
      personality: 'Encouraging',
      language: 'en',
      voice: 'tf1',
      backstory: '',
      libraryImageId: null
    });
  };

  // Preview a voice sample
  // Sprint 207P — voiceId is now a TEEN_VOICES slot id ("tf1", "tm5", …).
  // We resolve it to its base OpenAI voice + pitch/speed modifiers, send
  // the base voice + speed to the backend, then apply pitch client-side
  // via Web Audio API for a more distinct teen voice.
  const handleVoicePreview = async (voiceId) => {
    // Stop any currently playing audio
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
    }

    // If clicking same voice that's playing, just stop it
    if (previewingVoice === voiceId) {
      setPreviewingVoice(null);
      return;
    }

    setPreviewingVoice(voiceId);

    const slot = TEEN_VOICES.find(v => v.id === voiceId);
    const baseVoice = slot?.baseVoice || 'nova';
    const speed = slot?.speed || 1.0;
    const pitch = slot?.pitch || 1.0;

    // Sprint 206 — report any media failure to the Command Centre.
    const reportFailure = (event_type, extra = {}) => {
      apiClient
        .request('/api/media-health/report', {
          method: 'POST',
          body: { event_type, context: { voice_id: voiceId, language: customization.language, ...extra } },
        })
        .catch(() => { /* never block UI on telemetry */ });
    };

    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const response = await fetch(`${API_URL}/api/tts/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          voice: baseVoice,
          language: customization.language,
          speed,
        })
      });

      const data = await response.json();

      if (data.success && data.audio) {
        // playbackRate stretches BOTH pitch and tempo. Combine slot.pitch
        // with slot.speed so the same OpenAI base voice produces
        // distinguishable teen-aged variants without an ElevenLabs round-trip.
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audio.playbackRate = Math.max(0.5, Math.min(2.0, pitch));
        setPreviewAudio(audio);

        audio.onended = () => {
          setPreviewingVoice(null);
        };

        audio.onerror = () => {
          setPreviewingVoice(null);
          reportFailure('voice_preview_error', { source: 'audio_element_onError' });
          toast.error('Voice preview failed to play — reported to admin.');
        };

        await audio.play();
      } else {
        setPreviewingVoice(null);
        reportFailure('tts_request_error', {
          source: 'tts_response_unsuccessful',
          message: (data && data.message) || 'unknown',
        });
        toast.error(`Voice preview failed: ${(data && data.message) || 'TTS service not available'}`);
      }
    } catch (error) {
      reportFailure('tts_request_error', { source: 'network_error', error: String(error).slice(0, 200) });
      setPreviewingVoice(null);
      toast.error('Voice preview failed — please try again. Issue has been reported.');
    }
  };

  const handleCreateAva = async () => {
    if (!customization.avaName) {
      alert('Please enter a name for your AvA');
      return;
    }

    // For new AvAs, require a face chosen from the curated library
    if (!editingAva && !selectedPreset && !customPhotoUrl) {
      alert('Please choose a face from the image library for your friend');
      return;
    }

    setCreating(true);
    try {
      const avaData = {
        name: customization.avaName,
        personality_type: customization.personality,
        ava_role: editingAva?.ava_role || (selectedPreset ? getAvaRole(selectedPreset.id) : 'friend'),
        is_primary: editingAva?.is_primary || userAvas.length === 0,
        avatar_config: {
          image_url: customPhotoUrl || selectedPreset?.image || editingAva?.avatar_url || null,
          ava_library_id: previewAvaLibId || customization.libraryImageId || selectedPreset?.id || editingAva?.avatar_config?.ava_library_id || null,
          seed: selectedPreset?.name || customization.avaName,
          language: customization.language,
          voice: customization.voice,
          backstory: customization.backstory,
          is_custom: !!customPhotoUrl || editingAva?.avatar_config?.is_custom
        }
      };

      let resultAva;
      if (editingAva) {
        // Update existing AvA
        resultAva = await apiClient.updateAva(editingAva.ava_id, avaData);
        // Update local state
        setUserAvas(prev => prev.map(a => a.ava_id === editingAva.ava_id ? resultAva : a));
        setEditingAva(null);
        // Founder-confirmed flow (Aug 2026): Save → conversation page opens
        // with that tutor (same as the original create flow ending).
        const slug = avaSlug(resultAva?.name);
        navigate(slug ? `/conversations/practice/${slug}` : '/conversations/chat', { state: { selectedAva: resultAva } });
      } else {
        // Create new AvA
        resultAva = await apiClient.createAva(avaData);
        // Navigate to chat with the new AvA — use shareable name-slug URL.
        // CR-11 EXT: preserve any ?challenge_id=… so the session created in
        // ConversationsChat binds to the right challenge_run.
        const slug = avaSlug(resultAva?.name);
        const chId = new URLSearchParams(window.location.search).get('challenge_id');
        const qs = chId ? `?challenge_id=${encodeURIComponent(chId)}` : '';
        navigate(slug ? `/conversations/practice/${slug}${qs}` : `/conversations/chat${qs}`, { state: { selectedAva: resultAva } });
      }

      // Jun 2026 — personalisation reveal: fire a lip-synced "I love the name
      // you chose" clip in the background; the chat screen plays it when ready.
      const revealLibId = avaData.avatar_config.ava_library_id;
      if (resultAva?.ava_id) {
        const slot = TEEN_VOICES.find(v => v.id === customization.voice);
        apiClient.request('/api/ava-video/trigger-moment', {
          method: 'POST',
          body: JSON.stringify({
            session_id: `reveal_${resultAva.ava_id}`,
            moment_type: 'reveal',
            text: `Hi! I'm ${customization.avaName} — I love the name you chose for me. I'm set up just the way you wanted, and I can't wait to get started!`,
            voice_id: slot?.baseVoice || 'nova',
            ava_library_id: revealLibId || null,
            ava_id: resultAva.ava_id,
          }),
        }).catch(() => {});
        try { localStorage.setItem(`hibff_reveal_pending_${resultAva.ava_id}`, '1'); } catch (e) { /* no-op */ }
      }

      // Reset form
      setCustomPhotoUrl(null);
      setSelectedPreset(null);
      setCustomization({
        userName: user?.name?.split(' ')[0] || '',
        avaName: '',
        personality: 'Encouraging',
        language: 'en',
        voice: 'tf1',
        backstory: '',
        libraryImageId: null
      });
    } catch (error) {
      console.error('Failed to save AvA:', error);
      const errorMsg = error.message || 'Failed to save AvA';
      // Sprint 207AA — replace the native browser alert() with a centered
      // red-background black-text modal so safeguarding errors are obvious.
      if (errorMsg.includes('limit')) {
        setSaveErrorModal('You have reached the maximum number of AvAs for your subscription tier. Please upgrade or delete an existing AvA.');
      } else {
        setSaveErrorModal(errorMsg);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAva = async (avaId, avaName, e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete ${avaName}? This cannot be undone.`)) {
      try {
        await apiClient.deleteAva(avaId);
        setUserAvas(prev => prev.filter(a => a.ava_id !== avaId));
      } catch (error) {
        console.error('Failed to delete AVA:', error);
        alert('Failed to delete AVA. Please try again.');
      }
    }
  };

  const handleSetPrimary = async (avaId) => {
    try {
      await apiClient.request(`/api/avas/${avaId}/set-primary`, { method: 'POST' });
      setUserAvas(prev => prev.map(a => ({ ...a, is_primary: a.ava_id === avaId })));
    } catch (error) {
      console.error('Failed to set primary:', error);
    }
  };


  const handleSelectExistingAva = (ava) => {
    const slug = avaSlug(ava?.name);
    // CR-11 EXT: forward ?challenge_id=… so the bound challenge_run survives
    // the AvA-picker hop.
    const chId = new URLSearchParams(window.location.search).get('challenge_id');
    const qs = chId ? `?challenge_id=${encodeURIComponent(chId)}` : '';
    navigate(slug ? `/conversations/practice/${slug}${qs}` : `/conversations/chat${qs}`, { state: { selectedAva: ava } });
  };

  // (legacy preset grid removed Oct 2026 — getFilteredAvas no longer rendered)

  return (
    <DashboardLayout>
      <div className="min-h-[calc(100vh-80px)] lg:h-[calc(100vh-80px)] flex flex-col min-w-0 overflow-x-hidden">
        {/* Header */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 flex-shrink-0 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">
            My <span className="text-purple-600">Practice Sessions</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Each AvA helps you practise different social skills for real life</p>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row lg:overflow-hidden min-h-0 min-w-0">
          {/* Left Panel - Ava Selection */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 min-w-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* User's Existing AVAs */}
            {userAvas.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Star className="text-amber-500" size={20} />
                  My Trainers
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Aug 2026 — 9-tutor (3x3) layout: Maya, Rebecca, Neo on
                    row one, then the six specialist tutors in canonical
                    order. Render-time sort so API order is irrelevant. */}
                {[...userAvas].sort((a, b) => {
                  const rank = (x) => {
                    const isMayaX = (x.avatar_config?.preset === 'maya_default')
                      || (x.name === 'Maya' && ((x.seeded_by || '').includes('maya') || x.is_primary));
                    if (isMayaX) return 0;
                    const isRebeccaX = (x.avatar_config?.preset === 'rebecca_default')
                      || (x.name === 'Rebecca' && x.is_fixed);
                    if (isRebeccaX) return 1;
                    const isNeoX = (x.avatar_config?.preset === 'neo_default');
                    if (isNeoX) return 2;
                    const specX = SPECIALIST_META[x.avatar_config?.preset];
                    if (specX) return specX.order;
                    return 9;
                  };
                  return rank(a) - rank(b);
                }).map((ava) => {
                  // Sprint 207R — Maya is the founder-mandated, immutable
                  // Practice Manager. Detect by preset OR seed marker.
                  const isMaya = (
                    (ava.avatar_config?.preset === 'maya_default')
                    || (ava.name === 'Maya' && (ava.seeded_by || '').includes('maya'))
                    || (ava.name === 'Maya' && ava.is_primary)
                  );
                  // Feb 2026 — Rebecca is the second fixed anchor trainer
                  // (Science & Technology Tutor). Same detection rules as
                  // Maya: preset OR explicit name.
                  const isRebecca = (
                    (ava.avatar_config?.preset === 'rebecca_default')
                    || (ava.name === 'Rebecca' && ava.is_fixed)
                  );
                  // Feb 2026 — Neo is the Practice Friend for challenges.
                  // Unlike Maya/Rebecca, the teen owns his IDENTITY
                  // (name, portrait, voice, backstory) — only the
                  // challenges scripts stay founder-authored. So we detect
                  // Neo strictly by preset (survives rename) and let him
                  // keep the standard edit/delete toolbar.
                  const isNeo = (ava.avatar_config?.preset === 'neo_default');
                  // Aug 2026 — specialist tutors: teen can edit (image,
                  // voice, name) but never delete or set-primary, exactly
                  // like Neo. Subtitle keeps the topic legible after rename.
                  const specMeta = SPECIALIST_META[ava.avatar_config?.preset];
                  const isSpecialist = !!specMeta;
                  let _label;
                  let _subtitle = null;
                  if (isMaya) {
                    _label = 'Maya';
                    _subtitle = 'Your Guide';
                  } else if (isRebecca) {
                    _label = 'Rebecca Science & Technology Tutor';
                  } else if (isNeo) {
                    // Teen-chosen name (or "Neo" default), with a
                    // permanent "Practice Friend" subtitle so the
                    // challenges role stays legible even after rename.
                    _label = ava.name || 'Neo';
                    _subtitle = 'Practice Friend';
                  } else if (isSpecialist) {
                    _label = ava.name || 'AvA';
                    _subtitle = specMeta.subtitle;
                  } else {
                    // Feb 2026 — strip the demographic description from
                    // teen-chosen AvAs. The teen named the AvA, so show
                    // ONLY the name (no "Female age 14 • Steady" string).
                    _label = ava.name || 'AvA';
                  }
                  const _idleUrl = trainerIdleLoops[ava.ava_id];
                  return (
                  <div
                    key={ava.ava_id}
                    className="group relative p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:shadow-lg transition-all"
                    data-testid={`existing-ava-${ava.ava_id}`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectExistingAva(ava)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectExistingAva(ava); } }}
                      className="w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-lg"
                    >
                      <div className="relative">
                        {_idleUrl ? (
                          <IdleLoopVideo
                            src={_idleUrl}
                            poster={ava.avatar_url}
                            className="w-full aspect-square rounded-lg mb-3 bg-gray-100 object-cover"
                            testId={`trainer-idle-loop-${ava.ava_id}`}
                          />
                        ) : (
                          <img
                            src={ava.avatar_url}
                            alt={_label}
                            className="w-full aspect-square rounded-lg mb-3 bg-gray-100 object-cover"
                          />
                        )}
                        {/* AI Badge */}
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] font-bold bg-black/70 text-white rounded">
                          AI
                        </span>
                        {/* 2026-07-03 — one-click voice preview on every AvA
                            card, including Maya + Rebecca. Reuses the same
                            handleVoicePreview / TTS pipeline as the voice
                            picker below, no new audio plumbing. */}
                        {ava.avatar_config?.voice && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVoicePreview(ava.avatar_config.voice); }}
                            className={`absolute bottom-1 left-1 p-1.5 rounded-full shadow-md transition-all ${previewingVoice === ava.avatar_config.voice ? 'bg-purple-600 text-white scale-110' : 'bg-white/95 text-purple-700 hover:bg-purple-600 hover:text-white hover:scale-110'}`}
                            title={previewingVoice === ava.avatar_config.voice ? `Stop preview` : `Preview ${_label}'s voice`}
                            data-testid={`preview-voice-${ava.ava_id}`}
                          >
                            {previewingVoice === ava.avatar_config.voice ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Volume2 size={13} />
                            )}
                          </button>
                        )}
                      </div>
                      <p className={`text-sm font-medium ${isMaya ? 'text-purple-700 font-bold' : (isRebecca ? 'text-blue-700 font-bold' : (isNeo ? 'text-emerald-700 font-bold' : 'text-gray-900'))}`} data-testid={`trainer-label-${ava.ava_id}`}>{_label}</p>
                      {_subtitle && (
                        <p className="text-xs text-emerald-600 font-medium mt-0.5" data-testid={`trainer-subtitle-${ava.ava_id}`}>
                          {_subtitle}
                        </p>
                      )}
                      {matchedTypes.includes(ava.personality_type) && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full" style={{ backgroundColor: '#7B4FD815', color: '#7B4FD8' }} data-testid="ocean-match-label">
                          <Star size={10} /> Matched to your profile
                        </span>
                      )}
                    </div>
                    {ava.is_primary && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        Primary
                      </span>
                    )}
                    {/* Sprint 207R — Maya AvA is fixed: hide edit / delete /
                        set-primary buttons. She can only be changed from the
                        Command Centre. All OTHER trainers keep the full toolbar.
                        Feb 2026 — same protection extended to Rebecca. */}
                    {!isMaya && !isRebecca && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        {/* Neo + specialists: the pencil (rename / swap
                            portrait / voice / backstory) is the ONLY action
                            a teen can take — always visible per founder ask. */}
                        {!ava.is_primary && !isNeo && !isSpecialist && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(ava.ava_id); }}
                            className="p-1.5 bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200"
                            title="Set as Primary"
                            data-testid={`set-primary-${ava.ava_id}`}
                          >
                            <Star size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleEditAva(ava, e)}
                          className="p-1.5 bg-white/95 text-blue-600 rounded-full shadow-md hover:bg-blue-100"
                          title="Edit AvA"
                          data-testid={`edit-ava-${ava.ava_id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        {!isNeo && !isSpecialist && (
                          <button
                            onClick={(e) => handleDeleteAva(ava.ava_id, ava.name, e)}
                            className="p-1.5 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                            title="Delete AvA"
                            data-testid={`delete-ava-${ava.ava_id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                    {isMaya && (
                      <span
                        className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-purple-100 text-purple-700 uppercase tracking-wide"
                        data-testid={`maya-fixed-badge-${ava.ava_id}`}
                        title="Maya is your Practice Manager — only the Command Centre can change her"
                      >
                        Fixed
                      </span>
                    )}
                    {isRebecca && (
                      <span
                        className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-700 uppercase tracking-wide"
                        data-testid={`rebecca-fixed-badge-${ava.ava_id}`}
                        title="Rebecca is your Science & Technology Tutor — only the Command Centre can change her"
                      >
                        Fixed
                      </span>
                    )}
                    <div className="absolute inset-0 bg-purple-500/0 group-hover:bg-purple-500/5 rounded-xl transition-colors pointer-events-none flex items-center justify-center">
                      <ChevronRight className="text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                    </div>
                  </div>
                  );
                })}
                </div>
              </div>
            )}

            {/* Sprint 207X — My Completed Milestones ribbon */}
            <MilestoneRibbon />

            {/* Photo uploads removed (founder safeguarding directive, Jun 2026):
                friends are designed ONLY from the curated image library below. */}

            {/* Search & legacy preset catalogue removed (Oct 2026) — every
                non-teen user now browses the curated tutor library below,
                exactly like the teen Tutors tab (adult isolation enforced
                server-side). */}

            {/* Browse Image Library */}
            <div className="px-6 pb-6">
              {/* Unified picker (Oct 2026 — founder request): one heading,
                  Teens + adult age decades as sibling categories. */}
              <div className="mb-3">
                <h3 className="text-base sm:text-lg font-bold text-gray-800" data-testid="library-picker-heading">
                  Personalise Your AvA &amp; Tutor Images
                </h3>
                <p className="text-xs text-gray-500 mt-0.5" data-testid="library-picker-subtext">
                  These are looks, not people — choose the face you want{isTeen ? ' for your own Friend or your Tutor' : ' for your Tutor or your own AvA friend'}.
                </p>
              </div>
              <div className="flex gap-2 mb-3" data-testid="library-category-chips">
                {isTeen && (
                  <button
                    onClick={() => { setLibRoleType('friend'); setLibAgeRange(''); setLibPage(1); }}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      libRoleType === 'friend'
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-purple-300'
                    }`}
                    data-testid="category-teens"
                  >
                    Teens
                  </button>
                )}
                {['20s', '30s', '40s', '50s'].map((d) => {
                  const active = (!isTeen || libRoleType === 'tutor') && libAgeRange === d;
                  return (
                    <button
                      key={d}
                      onClick={() => {
                        if (isTeen) setLibRoleType('tutor');
                        setLibAgeRange(active ? '' : d);
                        setLibPage(1);
                      }}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        active
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300'
                      }`}
                      data-testid={`tutor-age-${d}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
                  {/* Male / Female buttons */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                    <button
                      onClick={() => {
                        setLibCategory('');
                        setLibGender(libGender === 'male' ? '' : 'male');
                        setLibPage(1);
                      }}
                      className={`py-3 rounded-xl font-semibold text-sm transition-all border-2 ${
                        libGender === 'male'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                      }`}
                      data-testid="teen-filter-male"
                    >
                      Male
                    </button>
                    <button
                      onClick={() => {
                        setLibCategory('');
                        setLibGender(libGender === 'female' ? '' : 'female');
                        setLibPage(1);
                      }}
                      className={`py-3 rounded-xl font-semibold text-sm transition-all border-2 ${
                        libGender === 'female'
                          ? 'bg-pink-600 text-white border-pink-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-pink-300'
                      }`}
                      data-testid="teen-filter-female"
                    >
                      Female
                    </button>
                  </div>

              {(isTeen || showLibrary) && (
                <div data-testid="image-library">
                  {/* Image Grid */}
                  {libLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-400" size={24} /></div>
                  ) : libraryImages.length === 0 ? (
                    <p className="text-center text-gray-400 py-8 text-sm">No images found. Try different filters.</p>
                  ) : (
                    <>
                      {libPageState && (
                        <div className="text-center mb-4">
                          <h3 className="text-lg font-bold text-gray-800" data-testid="lib-page-state">{libPageState}</h3>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {libraryImages.map(img => (
                          <div
                            key={img.image_id}
                            className={`group relative p-3 bg-white rounded-xl border-2 transition-all cursor-pointer ${
                              customPhotoUrl === img.image_url
                                ? 'border-purple-500 shadow-lg ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
                            }`}
                            onClick={() => {
                              setCustomPhotoUrl(img.image_url);
                              setCustomization(prev => ({ ...prev, libraryImageId: img.image_id }));
                              scrollToCustomize();
                            }}
                            data-testid={`lib-image-${img.image_id}`}
                          >
                            <div className="relative">
                              <img
                                src={img.image_url}
                                alt={isCorporateUser && libPageState ? `${libPageState} Practice AvA` : img.name}
                                className="w-full aspect-square rounded-lg bg-gray-100 object-cover"
                                loading="lazy"
                              />
                              <span className="absolute bottom-0 right-0 px-1.5 py-0.5 text-[10px] font-bold bg-black/70 text-white rounded">AI</span>
                            </div>
                            {/* Sprint 207X — text descriptions below image stripped. Images speak for themselves. */}
                            {customPhotoUrl === img.image_url && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                <Check size={14} className="text-white" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Pagination */}
                  {libTotal > 24 && (
                    <div className="flex justify-center items-center gap-3 mt-4 mb-2 py-3 bg-gray-50 rounded-xl">
                      <button
                        onClick={() => setLibPage(p => Math.max(1, p - 1))}
                        disabled={libPage <= 1}
                        className="px-4 py-2 text-sm font-medium border-2 rounded-xl disabled:opacity-30 hover:bg-white transition-colors"
                        data-testid="lib-page-prev"
                      >
                        Prev
                      </button>
                      <span className="px-3 py-1 text-sm font-medium text-gray-700" data-testid="lib-page-indicator">
                        {libPageState
                          ? `${libPageState} — Page ${libPage} of ${Math.ceil(libTotal / 24)}`
                          : `Page ${libPage} of ${Math.ceil(libTotal / 24)}`}
                      </span>
                      <button
                        onClick={() => setLibPage(p => p + 1)}
                        disabled={libraryImages.length < 24}
                        className="px-4 py-2 text-sm font-medium border-2 rounded-xl disabled:opacity-30 hover:bg-white transition-colors bg-purple-50 border-purple-200 text-purple-700"
                        data-testid="lib-page-next"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Customization (Sprint 207X — widened + spaced) */}
          <div ref={customizePanelRef} className="w-full lg:w-[28rem] xl:w-[30rem] border-t lg:border-t-0 lg:border-l border-gray-200 bg-white lg:overflow-y-auto" data-testid="customize-panel">
            <div className="p-5 sm:p-7">
              <div className="mb-4 sm:mb-6 text-center">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {editingAva ? (<>Edit Your<br />Practice Friend</>) : (<>Design Your Perfect<br />Practice Friend</>)}
                </h2>
                {editingAva && (
                  <button
                    onClick={handleCancelEdit}
                    className="text-sm text-gray-500 hover:text-gray-700 mt-2"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Preview — Sprint 207X+ — enlarged from 240px → 360px to fill the widened sidebar */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 text-center">
                {selectedPreset || customPhotoUrl ? (
                  <>
                    <div className="relative max-w-[340px] sm:max-w-[360px] mx-auto">
                      {previewVideoUrl ? (
                        <IdleLoopVideo
                          src={previewVideoUrl}
                          poster={customPhotoUrl || getAvaUrl(selectedPreset)}
                          className="w-full aspect-[3/4] mb-4 rounded-xl shadow-lg object-cover"
                          testId="ava-preview-video"
                        />
                      ) : (
                        <img
                          src={customPhotoUrl || getAvaUrl(selectedPreset)}
                          alt={customization.avaName || selectedPreset?.name || 'AI Buddy'}
                          className="w-full aspect-[3/4] mb-4 rounded-xl shadow-lg object-cover"
                          data-testid="ava-preview-image"
                        />
                      )}
                      {/* AI Badge on preview */}
                      <span className="absolute top-3 right-3 px-2 py-1 text-xs font-bold bg-black/80 text-white rounded-lg flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.912 5.813a2 2 0 001.272 1.272L21 12l-5.813 1.912a2 2 0 00-1.272 1.272L12 21l-1.912-5.813a2 2 0 00-1.272-1.272L3 12l5.813-1.912a2 2 0 001.272-1.272L12 3z"/></svg>
                        AI
                      </span>
                      {previewAnimating && !previewVideoUrl && (
                        <span className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1.5 text-xs font-semibold bg-black/70 text-white rounded-full flex items-center gap-1.5 whitespace-nowrap" data-testid="ava-preview-animating">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          Bringing your AvA to life…
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-lg text-gray-900 text-center" data-testid="customize-preview-label">
                      {customization.avaName
                        || (selectedPreset
                          ? `${presetDemographicLabel(selectedPreset)} • ${selectedPreset.personality}`
                          : 'AI Buddy')}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-full aspect-[3/4] max-w-[340px] sm:max-w-[360px] mx-auto mb-4 bg-gray-200 rounded-xl flex items-center justify-center">
                      <User size={64} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium text-sm sm:text-base">Select an AvA to preview</p>
                    <p className="text-xs sm:text-sm text-gray-400">Click any AvA {window.innerWidth >= 1024 ? 'on the left' : 'above'}</p>
                  </>
                )}
              </div>

              {/* Form Fields — Sprint 207X split into two breathable sections */}
              <div className="space-y-8">

                {/* ───── Section A: Profile Identity ───── */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-purple-200 to-transparent" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-purple-700">Profile Identity</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-purple-200 to-transparent" />
                  </div>
                  <div className="space-y-6">
                    {/* User's Name */}
                    <div>
                      <label className="block text-base font-bold text-gray-700 mb-2">
                        Your 1st Name Only
                      </label>
                      <input
                        type="text"
                        value={customization.userName}
                        onChange={(e) => setCustomization(prev => ({ ...prev, userName: e.target.value }))}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="user-name-input"
                      />
                    </div>

                    {/* AvA Name */}
                    <div>
                      <label className="block text-base font-bold text-gray-700 mb-2">
                        Give Your AvA a 1st Name Only
                      </label>
                      <input
                        type="text"
                        value={customization.avaName}
                        onChange={(e) => setCustomization(prev => ({ ...prev, avaName: e.target.value }))}
                        placeholder="Peter"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                        data-testid="ava-name-input"
                      />
                    </div>

                    {/* Backstory removed (Aug 2026) — tutors are discipline-locked
                        under the new model, so a personal backstory served no
                        purpose. State plumbing left intact for legacy configs. */}
                  </div>
                </div>

                {/* ───── Section B: Voice & Character ───── */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-pink-200 to-transparent" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-pink-700">Voice & Character</span>
                    <div className="h-px flex-1 bg-gradient-to-l from-pink-200 to-transparent" />
                  </div>
                  <div className="space-y-6">
                    {/* Personality */}
                    <div>
                      <label className="block text-base font-bold text-gray-700 mb-2">
                        Personality
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {(PERSONALITY_BY_TAB[activeCategory]
                          ? ALL_PERSONALITY_OPTIONS.filter(opt => PERSONALITY_BY_TAB[activeCategory].includes(opt.value))
                          : ALL_PERSONALITY_OPTIONS
                        ).map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setCustomization(prev => ({ ...prev, personality: opt.value }))}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${
                              customization.personality === opt.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`personality-${opt.value}`}
                          >
                            <p className="font-medium text-gray-900 text-sm">{opt.label}</p>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Language */}
                    <div>
                      <label className="block text-base font-bold text-gray-700 mb-2">
                        <Globe size={16} className="inline mr-2" />
                        Language
                      </label>
                      <select
                        value={customization.language}
                        onChange={(e) => setCustomization(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        data-testid="language-select"
                      >
                        {LANGUAGES.map((lang) => (
                          <option key={lang.value} value={lang.value}>{lang.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Voice Selection */}
                    <div>
                      <label className="block text-base font-bold text-gray-700 mb-2">
                        <span className="inline-flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M12 2c-1.7 0-3 1.2-3 2.6v6.8c0 1.4 1.3 2.6 3 2.6s3-1.2 3-2.6V4.6C15 3.2 13.7 2 12 2z"/><path d="M19 10v1a7 7 0 0 1-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                          Voice
                          <span className="ml-2 text-xs text-purple-500 font-normal">(click speaker to preview)</span>
                        </span>
                      </label>
                      <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1" data-testid="voice-picker">
                        {(() => {
                          // Split into Female (left column) and Male (right
                          // column) so the gender separation is visual, not
                          // just a label. Slot numbering remains 1-N per
                          // gender so the labels read "Female Voice 3" etc.
                          const femaleVoices = TEEN_VOICES.filter(v => v.gender === 'Female');
                          const maleVoices = TEEN_VOICES.filter(v => v.gender === 'Male');
                          const renderVoice = (voice, slotNumber) => {
                            const label = voice.label || `Voice ${slotNumber}`;
                            return (
                              <div
                                key={voice.id}
                                className={`relative p-2 rounded-xl border-2 transition-all ${
                                  customization.voice === voice.id
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 hover:border-purple-300'
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => setCustomization(prev => ({ ...prev, voice: voice.id }))}
                                  className="w-full text-left pr-7"
                                  data-testid={`voice-${voice.id}`}
                                >
                                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold inline-block text-center ${
                                      voice.gender === 'Female' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'
                                    }`} style={{ minWidth: 52 }}>{voice.gender}</span>
                                    <span className="text-sm font-semibold text-gray-900">{label}</span>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVoicePreview(voice.id);
                                  }}
                                  className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
                                    previewingVoice === voice.id
                                      ? 'bg-purple-500 text-white animate-pulse'
                                      : 'bg-gray-100 text-gray-500 hover:bg-purple-100 hover:text-purple-600'
                                  }`}
                                  title={previewingVoice === voice.id ? 'Stop preview' : 'Preview voice'}
                                  data-testid={`voice-preview-${voice.id}`}
                                >
                                  {previewingVoice === voice.id ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Volume2 size={14} />
                                  )}
                                </button>
                              </div>
                            );
                          };
                          return (
                            <>
                              <div className="space-y-2" data-testid="voice-column-female">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-pink-600 mb-1 px-1">Female</div>
                                {femaleVoices.map((v, i) => renderVoice(v, i + 1))}
                              </div>
                              <div className="space-y-2" data-testid="voice-column-male">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-1 px-1">Male</div>
                                {maleVoices.map((v, i) => renderVoice(v, i + 1))}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Create/Save Button */}
              <button
                onClick={handleCreateAva}
                disabled={!customization.avaName || creating}
                className="w-full mt-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="create-ava-btn"
              >
                {creating ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {editingAva ? 'Saving...' : 'Creating...'}
                  </>
                ) : editingAva ? (
                  <>
                    <Check size={20} />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    Create My AvA
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sprint 207AA — centered RED background / BLACK text save-error modal,
          replaces native window.alert() for safeguarding-style refusals. */}
      {saveErrorModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
          onClick={() => setSaveErrorModal(null)}
          data-testid="ava-save-error-modal"
          role="alertdialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-2xl shadow-2xl max-w-md w-full p-6"
            style={{ backgroundColor: '#FECACA', border: '3px solid #B91C1C' }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#B91C1C' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3 className="text-xl font-extrabold" style={{ color: '#000000' }} data-testid="ava-save-error-title">
                Couldn&apos;t save your AvA
              </h3>
            </div>
            <p className="text-base font-semibold leading-relaxed mb-5" style={{ color: '#000000' }} data-testid="ava-save-error-body">
              {saveErrorModal}
            </p>
            <button
              onClick={() => setSaveErrorModal(null)}
              className="w-full py-3 rounded-lg font-bold text-base transition-colors"
              style={{ backgroundColor: '#000000', color: '#FFFFFF' }}
              data-testid="ava-save-error-dismiss"
            >
              Got it — try again
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AvALibrary;
